import React, { useEffect, useMemo, useState } from 'react';
import { GetVorgang200ResponseVorgangAbarbeitungenInnerSchritteInner } from '@/adapter/opm-cockpit-backend/__generated';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  MonacoJsonEditor,
  MonacoLanguage,
} from '@/components/ui/monaco-json-editor';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { DokumentVorschauButton } from '@/features/dokumentVorschau/DokumentVorschauButton';
import type { ValidationError } from '@/types';

interface VorgangDataEditorProps {
  rawEventData: string;
  ausgewaehlterSchritt: GetVorgang200ResponseVorgangAbarbeitungenInnerSchritteInner;
  previousPayload?: string;
  validationErrors: ValidationError[];
  isValid: boolean;
  isLockedByCurrentUser: boolean;
  canEditWithoutLock?: boolean;
  isDeleted?: boolean;
  onEventDataChange: (value: string) => void;
  validateJson: (jsonString: string) => void;
  schema: object;
  canPreview?: boolean;
  vorgangId: string;
}

export const VorgangDataEditor: React.FC<VorgangDataEditorProps> = ({
  rawEventData,
  ausgewaehlterSchritt,
  validationErrors,
  previousPayload,
  isValid,
  isLockedByCurrentUser,
  canEditWithoutLock = false,
  isDeleted = false,
  onEventDataChange,
  validateJson,
  schema,
  canPreview = false,
  vorgangId,
}) => {
  const [tabSelection, setTabSelection] = useState<string>('inData');
  const successPayload = ausgewaehlterSchritt.successPayload;

  useEffect(() => {
    setTabSelection('inData');
  }, [ausgewaehlterSchritt.id]);

  const parsedEventData = useMemo(() => {
    try {
      return JSON.parse(rawEventData);
    } catch {
      return null;
    }
  }, [rawEventData]);

  const readOnly = (!isLockedByCurrentUser && !canEditWithoutLock) || isDeleted;

  const outputLanguage: MonacoLanguage =
    successPayload && successPayload['xml']
      ? MonacoLanguage.XML
      : MonacoLanguage.JSON;

  const outputText =
    outputLanguage === MonacoLanguage.XML
      ? successPayload['xml']
      : successPayload
        ? JSON.stringify(successPayload, null, 2)
        : null;

  const onClickDownload = () => {
    const element = document.createElement('a');
    const file = new Blob([outputText], {
      type: 'text/plain',
    });
    element.href = URL.createObjectURL(file);
    element.download = vorgangId + '_output.' + outputLanguage;
    document.body.appendChild(element); // Required for this to work in FireFox
    element.click();
  };

  return (
    <Tabs value={tabSelection}>
      <TabsList>
        <TabsTrigger value="inData" onClick={() => setTabSelection('inData')}>
          Datenstrom - eingehend
        </TabsTrigger>
        <TabsTrigger
          value="outData"
          disabled={!outputText}
          onClick={() => setTabSelection('outData')}
        >
          Datenstrom - ausgehend
        </TabsTrigger>
      </TabsList>
      <TabsContent value="inData">
        <div
          className={`bg-white border border-table-border rounded-lg p-6 ${isDeleted ? 'opacity-60' : ''}`}
        >
          <div className="flex justify-between gap-3">
            <h2 className="text-lg font-semibold text-text-primary">
              Event-Datenstrom (JSON)
              {readOnly ? ' - Vorschau' : ' - Bearbeitung'}
              {isDeleted && ' (Gelöscht)'}
            </h2>
            <div>
              <Badge variant={isValid ? 'default' : 'destructive'}>
                {isValid ? 'Gültig' : 'Ungültig'}
              </Badge>
              {isDeleted && <Badge variant="secondary">Gelöscht</Badge>}
              {canPreview && (
                <DokumentVorschauButton
                  dokumentQuelldaten={
                    parsedEventData?.dokumentQuelldaten ?? null
                  }
                  versandArt={parsedEventData?.versandArt ?? null}
                  disabled={!isValid || isDeleted}
                />
              )}
            </div>
          </div>
          <div className="space-y-2">
            <label
              htmlFor="event-data"
              className="text-sm font-medium text-text-secondary"
            >
              {isDeleted
                ? 'Gelöschte Event-Daten (nur Anzeige):'
                : readOnly
                  ? 'Vorschau der Event-Daten:'
                  : 'Bearbeiten Sie die Event-Daten:'}
            </label>
            <MonacoJsonEditor
              value={rawEventData}
              onChange={onEventDataChange}
              previousValue={previousPayload}
              errors={validationErrors}
              readOnly={readOnly}
              className="min-h-[400px]"
              height={400}
              validateJson={validateJson}
              schema={schema}
            />
          </div>
        </div>
      </TabsContent>
      <TabsContent value="outData">
        <div
          className={`bg-white border border-table-border rounded-lg p-6 space-y-6 ${isDeleted ? 'opacity-60' : ''}`}
        >
          <div className="flex justify-between gap-3">
            <h2 className="text-lg font-semibold text-text-primary">
              Ausgangsdatenstrom ({outputLanguage.toUpperCase()})
            </h2>
            <Button
              type="button"
              variant="outline"
              size="sm"
              title={'Download'}
              onClick={onClickDownload}
            >
              Download
            </Button>
          </div>
          <MonacoJsonEditor
            value={outputText ?? ''}
            language={outputLanguage}
            readOnly
            className="min-h-[400px]"
            height={400}
          />
        </div>
      </TabsContent>
    </Tabs>
  );
};
